import { GetBookingsParams } from "@/types/admin/booking-type";
import { GetCustomersParams } from "@/types/admin/customer-type";
import { GetPartnersParams } from "@/types/admin/partner-type";
import { UpdateCurrencyRatePayload } from "@/types/admin/currencyRates";
import {
  GetNewslettersParams,
  GetContactMessagesParams,
} from "@/types/admin/newsletter-contact-type";
import {
  CreateNewsPayload,
  UpdateNewsPayload,
  GetNewsParams,
} from "@/types/admin/news-types";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";

const getAdminToken = () => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("adminAccessToken");
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
});

// ── helper: page/pageSize → offset/count ─────────────────────────────────────
const toOffsetCount = (page = 1, pageSize = 20) => ({
  offset: (page - 1) * pageSize,
  count: pageSize,
});

// ── Bookings ──────────────────────────────────────────────────────────────────
export const bookingService = {
  async getBookings(params: GetBookingsParams) {
    const { page, pageSize, search, status, paymentStatus, hotelId, sortBy, sortOrder } = params;
    const { offset, count } = toOffsetCount(page, pageSize);

    const query = new URLSearchParams({
      offset: offset.toString(),
      count: count.toString(),
      sortBy: sortBy || "created_at",
      sortOrder: sortOrder || "desc",
    });

    if (search) query.set("search", search);
    if (status) query.set("status", status);
    if (paymentStatus !== undefined) query.set("paymentStatus", paymentStatus.toString());
    if (hotelId !== undefined) query.set("hotelId", hotelId.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/admin/bookings?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch bookings");
    return response.json();
  },
};

// ── Customers ─────────────────────────────────────────────────────────────────
export const customerService = {
  async getCustomers(params: GetCustomersParams) {
    const { page, pageSize, search, status, emailVerified, sortBy, sortOrder } = params;
    const { offset, count } = toOffsetCount(page, pageSize);

    const query = new URLSearchParams({
      offset: offset.toString(),
      count: count.toString(),
      sortBy: sortBy || "creationdate",
      sortOrder: sortOrder || "desc",
    });

    if (search) query.set("search", search);
    if (status !== undefined) query.set("status", status.toString());
    if (emailVerified !== undefined) query.set("emailVerified", emailVerified.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/admin/customers?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch customers");
    return response.json();
  },
};

// ── Partners ──────────────────────────────────────────────────────────────────
export const partnerService = {
  async getPartners(params: GetPartnersParams) {
    const { page, pageSize, search, status, active, emailVerified, sortBy, sortOrder } = params;
    const { offset, count } = toOffsetCount(page, pageSize);

    const query = new URLSearchParams({
      offset: offset.toString(),
      count: count.toString(),
      sortBy: sortBy || "createddate",
      sortOrder: sortOrder || "desc",
    });

    if (search) query.set("search", search);
    if (status) query.set("status", status);
    if (active !== undefined) query.set("active", active.toString());
    if (emailVerified !== undefined) query.set("emailVerified", emailVerified.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/admin/partners?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch partners");
    return response.json();
  },
};

// ── Newsletters ───────────────────────────────────────────────────────────────
export const newsletterService = {
  async getNewsletters(params: GetNewslettersParams) {
    const { page, pageSize, search, isActive, sortBy, sortOrder } = params;
    const { offset, count } = toOffsetCount(page, pageSize);

    const query = new URLSearchParams({
      offset: offset.toString(),
      count: count.toString(),
      sortBy: sortBy || "creationdate",
      sortOrder: sortOrder || "desc",
    });

    if (search) query.set("search", search);
    if (isActive !== undefined) query.set("isActive", isActive.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/admin/newsletter?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch newsletters");
    return response.json();
  },
};

// ── Contact Messages ──────────────────────────────────────────────────────────
export const contactMessageService = {
  async getContactMessages(params: GetContactMessagesParams) {
    const { page, pageSize, search, status, sortBy, sortOrder } = params;
    const { offset, count } = toOffsetCount(page, pageSize);

    const query = new URLSearchParams({
      offset: offset.toString(),
      count: count.toString(),
      sortBy: sortBy || "creationdate",
      sortOrder: sortOrder || "desc",
    });

    if (search) query.set("search", search);
    if (status !== undefined) query.set("status", status.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/admin/contact-messages?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch contact messages");
    return response.json();
  },
};

// ── Currency Rates ─────────────────────────────────────────────────────────────
export const currencyRatesService = {
  async getCurrentRate() {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/currency-rates/usd-lkr`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch currency rate");
    return response.json();
  },

  async updateRate(payload: UpdateCurrencyRatePayload) {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/currency-rates/usd-lkr`,
      {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error("Failed to update currency rate");
    return response.json();
  },

  async getRateHistory() {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/currency-rates/usd-lkr/history`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch rate history");
    return response.json();
  },
};

// ── News ───────────────────────────────────────────────────────────────────────
export const newsService = {
  async getNews(params: GetNewsParams = {}) {
    const { page = 1, pageSize = 9, search, category, isPublished, sortBy, sortOrder } = params;

    const query = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (search) query.set("q", search);          // ← public API uses "q"
    if (category) query.set("category", category);
    if (isPublished !== undefined) query.set("isPublished", isPublished.toString());
    if (sortBy) query.set("sortBy", sortBy);
    if (sortOrder) query.set("sortOrder", sortOrder);

    const response = await fetch(
      `${API_BASE_URL}/api/news?${query}`,        // ← public endpoint, no /admin/
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch news");
    return response.json();
  },

  async createNews(payload: CreateNewsPayload) {
    const response = await fetch(`${API_BASE_URL}/api/admin/news`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error("Failed to create news");
    return response.json();
  },

  async updateNews(uid: number, payload: UpdateNewsPayload) {
    const response = await fetch(`${API_BASE_URL}/api/admin/news/${uid}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error("Failed to update news");
    return response.json();
  },

  async deleteNews(uid: number) {
    const response = await fetch(`${API_BASE_URL}/api/admin/news/${uid}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error("Failed to delete news");
    // return response.json();
  },
};
// ── Storage ───────────────────────────────────────────────────────────────────
export const storageService = {
  async presignUpload(fileName: string, contentType: string, folder: string) {
    const response = await fetch(`${API_BASE_URL}/api/Storage/presign-upload`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        fileName,
        contentType,
        folder,
        isPublic: true,
        cacheControl: "public, max-age=31536000",
      }),
    });
    if (!response.ok) throw new Error("Failed to get upload URL");
    return response.json(); // { uploadUrl, objectKey, publicCdnUrl, requiredHeaders }
  },
};


// ── Tour Package Bookings ─────────────────────────────────────────────────────
export const tourPackageBookingService = {
  async getBookings(params: {
    packageId?: string;
    bookingStatus?: string;
    paymentStatus?: string;
    customerEmail?: string;
    fromDate?: string;
    toDate?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.packageId) query.set("packageId", params.packageId);
    if (params.bookingStatus) query.set("bookingStatus", params.bookingStatus);
    if (params.paymentStatus) query.set("paymentStatus", params.paymentStatus);
    if (params.customerEmail) query.set("customerEmail", params.customerEmail);
    // omit fromDate/toDate entirely to avoid the DB param type error
    const qs = query.toString() ? `?${query.toString()}` : "";
    const response = await fetch(
      `${API_BASE_URL}/api/admin/tour-package-bookings${qs}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch tour package bookings");
    return response.json();
  },

async getCustomRequests(params: {
    packageId?: string;
    status?: string;
    customerEmail?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.packageId) query.set("packageId", params.packageId);
    if (params.status) query.set("status", params.status);
    if (params.customerEmail) query.set("customerEmail", params.customerEmail);
    const qs = query.toString() ? `?${query.toString()}` : "";
    const response = await fetch(
      `${API_BASE_URL}/api/admin/tour-package-bookings/custom-requests${qs}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch custom requests");
    return response.json();
  },

  async createPaymentLink(
    bookingUid: string,
    body: { paymentType: string; amountLkr: number; notes?: string }
  ) {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/tour-package-bookings/${bookingUid}/payment-links`,
      { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }
    );
    if (!response.ok) throw new Error("Failed to create payment link");
    return response.json();
  },

  async getPaymentLinks(bookingUid: string) {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/tour-package-bookings/${bookingUid}/payment-links`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch payment links");
    return response.json();
  },
 async updateBookingStatus(
    bookingUid: string,
    payload: {
      bookingStatus?: string;
      paymentStatus?: string;
      remarks?: string;
      modifiedBy?: string;
    }
  ) {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/tour-package-bookings/${bookingUid}/status`,
      {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error("Failed to update booking status");
    if (response.status === 204) return null; // no body to parse
    return response.json();
  },
};

// ── Tour Guides ───────────────────────────────────────────────────────────────
export const tourGuideService = {
  async getTourGuides(params: {
    page?: number;
    pageSize?: number;
    status?: string;
    district?: string;
  } = {}) {
    const { page = 1, pageSize = 20, status, district } = params;
    const query = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (status) query.set("status", status);
    if (district) query.set("district", district);

    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides?${query}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch tour guides");
    return response.json();
  },

  async getTourGuideById(tourGuideId: string) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides/${tourGuideId}`,
      { headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch tour guide details");
    return response.json();
  },

  async setUnderReview(tourGuideId: string) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides/${tourGuideId}/under-review`,
      { method: "POST", headers: authHeaders() }
    );
    if (!response.ok) throw new Error("Failed to set under review");
    return response.json();
  },

  async approveTourGuide(tourGuideId: string, adminNote: string) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides/${tourGuideId}/approve`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ adminNote }),
      }
    );
    if (!response.ok) throw new Error("Failed to approve tour guide");
    return response.json();
  },

  async requestChanges(
    tourGuideId: string,
    items: { field: string; message: string }[],
    adminNote: string
  ) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides/${tourGuideId}/request-changes`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ items, adminNote }),
      }
    );
    if (!response.ok) throw new Error("Failed to request changes");
    return response.json();
  },

  async rejectTourGuide(tourGuideId: string, reason: string) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/tour-guides/${tourGuideId}/reject`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason }),
      }
    );
    if (!response.ok) throw new Error("Failed to reject tour guide");
    return response.json();
  },
};